import React, { useState, useRef, useEffect } from 'react';
import { UploadCloud, CheckCircle2, AlertCircle, X, Mail, MessageSquareText, Sparkles, ImagePlus, Phone, MapPin } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const StepBadge = ({ n }) => (
  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#1B365D] text-white text-[11px] font-bold flex items-center justify-center">
    {n}
  </span>
);

const BulkOrderUpload = ({ isModal = false, onClose }) => {
  const { currentUser: user } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [guestContact, setGuestContact] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const fileInputRef = useRef(null);
  const notesStep = 3;

  // Prefill delivery details for logged-in users: their saved profile info
  // first, falling back to their most recent order's shipping address if
  // the profile itself doesn't have one yet. Stays fully editable either way.
  useEffect(() => {
    if (!user) return;

    if (user.phone) setPhone(user.phone);
    if (user.address) setAddress(user.address);
    if (user.city) setCity(user.city);

    if (!user.address || !user.phone) {
      api.get('/orders')
        .then((response) => {
          const lastOrder = response.data?.data?.orders?.[0];
          const shipping = lastOrder?.shippingDetails;
          if (!shipping) return;
          setPhone((prev) => prev || shipping.phone || '');
          setAddress((prev) => prev || shipping.address || '');
          setCity((prev) => prev || shipping.city || '');
        })
        .catch(() => {
          // No prior orders or request failed - leave fields as-is (editable, blank)
        });
    }
  }, [user]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type.startsWith('image/')) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setStatus('idle');
    } else {
      setErrorMessage('Please select a valid image file.');
      setStatus('error');
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setStatus('idle');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    if (!user && !guestContact) {
      setErrorMessage('Please provide an email or phone number so we can contact you.');
      setStatus('error');
      return;
    }

    setIsUploading(true);
    setStatus('idle');

    const formData = new FormData();
    formData.append('image', file);
    if (customerNotes) formData.append('customerNotes', customerNotes);
    if (!user && guestContact) formData.append('guestContactInfo', guestContact);
    if (user) {
      if (phone) formData.append('phone', phone);
      if (address) formData.append('address', address);
      if (city) formData.append('city', city);
    }

    try {
      await api.post('/bulk-orders', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setStatus('success');
      setFile(null);
      setPreview(null);
      setCustomerNotes('');
      setGuestContact('');
    } catch (error) {
      console.error('Upload failed:', error);
      setStatus('error');
      setErrorMessage(error.response?.data?.error || 'Failed to upload order. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={isModal ? "bg-white p-4 sm:p-6 md:p-8 relative" : "bg-white py-12 border-t border-gray-100"}>
      <div className={isModal ? "w-full" : "container mx-auto px-4 max-w-4xl"}>
        {isModal && (
          <button onClick={onClose} className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors">
            <X className="h-5 w-5" />
          </button>
        )}

        <div className="text-center mb-5 sm:mb-7 pr-8 sm:pr-0">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-[#1B365D] mb-2 tracking-tight flex items-center justify-center gap-2">
            Quick Bulk Order
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-secondary-500 flex-shrink-0" />
          </h2>
          <p className="text-[#1B365D]/60 max-w-lg mx-auto text-xs sm:text-sm md:text-base leading-relaxed">
            Don't have time to search for individual items? Snap a picture of your handwritten or printed list — our team takes it from there.
          </p>
        </div>

        <div className="bg-slate-50/70 rounded-2xl sm:rounded-3xl p-3.5 sm:p-5 md:p-7 border border-slate-100">
          {status === 'success' ? (
            <div className="text-center py-6 sm:py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-green-100 to-green-50 rounded-full mb-4 sm:mb-6">
                <CheckCircle2 className="h-8 w-8 sm:h-10 sm:w-10 text-green-500" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3 tracking-tight">Order Received Successfully!</h3>
              <p className="text-sm sm:text-base text-gray-500 mb-6 sm:mb-8 max-w-md mx-auto">Our team is reviewing your list and will contact you shortly to confirm the order details.</p>
              <button
                onClick={() => setStatus('idle')}
                className="px-8 py-3 bg-gradient-to-br from-secondary-500 to-secondary-700 text-white rounded-xl font-bold hover:shadow-lg hover:shadow-secondary-600/40 hover:-translate-y-0.5 transition-all duration-300"
              >
                Submit Another List
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-5 sm:mb-6">
                <label className="flex items-center gap-2 text-sm font-semibold text-[#1B365D] mb-2.5 sm:mb-3">
                  <StepBadge n={1} /> Upload Order Image
                </label>
                {!preview ? (
                  <div
                    className="group border-2 border-dashed border-gray-300 hover:border-secondary-500 rounded-2xl p-5 sm:p-8 md:p-10 flex flex-col items-center justify-center cursor-pointer bg-white hover:bg-secondary-50/40 transition-all duration-300"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="bg-gradient-to-br from-secondary-50 to-secondary-100 p-3 sm:p-4 rounded-full mb-3 sm:mb-4 shadow-sm group-hover:scale-110 group-hover:shadow-md transition-all duration-300">
                      <UploadCloud className="h-6 w-6 sm:h-8 sm:w-8 text-secondary-600" strokeWidth={2} />
                    </div>
                    <p className="text-[#1B365D] font-bold mb-1 group-hover:text-secondary-700 transition-colors text-sm sm:text-base md:text-lg text-center">Click to upload your image</p>
                    <p className="text-[11px] sm:text-xs text-[#1B365D]/50 font-medium mt-1 text-center">JPG, PNG or WEBP &middot; Max 10MB</p>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept="image/*"
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="rounded-2xl overflow-hidden border border-gray-200 bg-white">
                    <img src={preview} alt="Order Preview" className="w-full h-56 md:h-64 object-contain bg-gray-50" />
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500 truncate flex items-center gap-1.5 min-w-0">
                        <ImagePlus className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{file?.name}</span>
                      </span>
                      <button
                        type="button"
                        onClick={handleClearFile}
                        className="text-xs font-semibold text-secondary-600 hover:text-secondary-700 flex-shrink-0"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {!user && (
                <div className="mb-4 sm:mb-5">
                  <label htmlFor="guestContact" className="flex items-center gap-2 text-sm font-semibold text-[#1B365D] mb-2">
                    <StepBadge n={2} /> Email or Phone Number *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      id="guestContact"
                      value={guestContact}
                      onChange={(e) => setGuestContact(e.target.value)}
                      placeholder="So we can contact you about your order"
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-secondary-500/20 focus:border-secondary-500 outline-none transition-all duration-200"
                      required
                    />
                  </div>
                </div>
              )}

              {user && (
                <div className="mb-4 sm:mb-5">
                  <label className="flex items-center gap-2 text-sm font-semibold text-[#1B365D] mb-2">
                    <StepBadge n={2} /> Delivery Details
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Contact phone"
                        className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-secondary-500/20 focus:border-secondary-500 outline-none transition-all duration-200"
                      />
                    </div>
                    <div className="relative">
                      <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="City"
                        className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-secondary-500/20 focus:border-secondary-500 outline-none transition-all duration-200"
                      />
                    </div>
                  </div>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Delivery address"
                    className="w-full mt-3 px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-secondary-500/20 focus:border-secondary-500 outline-none transition-all duration-200"
                  />
                </div>
              )}

              <div className="mb-5 sm:mb-7">
                <label htmlFor="notes" className="flex items-center gap-2 text-sm font-semibold text-[#1B365D] mb-2 flex-wrap">
                  <StepBadge n={notesStep} /> Additional Notes <span className="text-[#1B365D]/40 font-normal">(Optional)</span>
                </label>
                <div className="relative">
                  <MessageSquareText className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400 pointer-events-none" />
                  <textarea
                    id="notes"
                    value={customerNotes}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                    placeholder="Any specific brands, quantities, or instructions..."
                    rows="3"
                    className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-secondary-500/20 focus:border-secondary-500 outline-none transition-all duration-200 resize-none"
                  ></textarea>
                </div>
              </div>

              {status === 'error' && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-700 rounded-xl flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{errorMessage}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={!file || isUploading}
                className={`w-full py-3.5 rounded-xl font-bold text-white transition-all duration-300 relative overflow-hidden group ${
                  !file || isUploading
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-br from-secondary-500 to-secondary-700 shadow-[0_4px_15px_-3px_rgba(220,38,38,0.4)] hover:shadow-[0_8px_25px_-5px_rgba(220,38,38,0.5)] hover:-translate-y-0.5'
                }`}
              >
                {file && !isUploading && (
                  <div className="absolute inset-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-[150%] skew-x-[-25deg] group-hover:translate-x-[250%] transition-transform duration-1000 ease-out" />
                )}
                <span className="relative z-10 flex items-center justify-center gap-2 drop-shadow-sm">
                  {isUploading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </>
                  ) : 'Submit Order List'}
                </span>
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkOrderUpload;
